import axios from 'axios';
import React, { createContext, useContext, useState } from 'react';
import {
  DropdownOption,
  Property,
  SubmitionError,
  SearchType,
  PropertyError,
  IndexedPropertyErrors,
} from './types';
import { useHistory } from 'react-router-dom';
import produce from 'immer';
import { useUserContext } from '../../../context/UserContext';
import qs from 'qs';

interface Props {
  children: React.ReactNode;
}

interface ContextType {
  properties: Property[];
  setProperties: React.Dispatch<React.SetStateAction<Property[]>>;
  onSubmit: (orderId: number) => void;
  errors: SubmitionError;
  propertyErrors: IndexedPropertyErrors;
  validateProperty: () => boolean;
  setAddedSearches: React.Dispatch<React.SetStateAction<any[]>>;
  addedSearches: any[];
  submitting: boolean;
  clientNote: string;
  setClientNote: React.Dispatch<React.SetStateAction<string>>;
  openErrorModal: boolean;
  setOpenErrorModal: React.Dispatch<React.SetStateAction<boolean>>;
  changePropertyInfo: (label: keyof Partial<Property>, value: any, index: number) => void;
  onCancel:() => void;
}

const createProperty = () => {
  const property: Property = {
    address: '',
    addressLine2: '',
    undeterminedLocality: false,
    zip: '',
    district: '',
    section: '',
    block: '',
    lot: '',
    qualifier: '',
    isDuplicate: false,
    includeMunicipalSearches: false,
    isValidated: false,
    rpadId: null,
    municipalityId: null,
    subMunicipalityId: null,
    duplicates: null,
    countyOnly: false,
    rpadPropertyId: '',
    printKey: '',
    owners: null,
    fullValidationNote: null,
    validationNote: '',
    preConvertedFiles: null,
    validationFiles: null,
    isResidential: null,
    unvalidatedOwner: null,
    selectedCounty: null,
    overrideMode: false,
    isNyc: false,
    hamletId: null,
    selectedCity: {
      county: '',
      municipality: '',
      municipalityId: null,
      municipalityType: '',
      stateCode: '',
      stateName: '',
      subMunicipality: '',
      subMunicipalityId: null,
      subMunicipalityType: '',
      swisCode: '',
      rpadSwisCode: '',
      countyId: null,
      id: null,
      municipalityTypeId: null,
      subMunicipalityTypeId: null,
      hamletId: null,
      hamletName: ''
    },
  };

  return property;
};
const AddPropertyContext = createContext<ContextType | undefined>(undefined);
const labelsMarkUnvalidated: (keyof Property)[] = ['address', 'block', 'section', 'qualifier', 'lot','addressLine2', ]

export const AddPropertyProvider = (props: Props) => {
  const { user } = useUserContext();
  const [properties, setProperties] = useState<Property[]>([createProperty()]);
  const [errors, setErrors] = useState<SubmitionError>(null);
  const [propertyErrors, setPropertyErrors] = useState<IndexedPropertyErrors>({});
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addedSearches, setAddedSearches] = useState<SearchType[]>([]);
  const [clientNote, setClientNote] = useState('');

  const { push } = useHistory();

  const validateForm = () => {
    const errors: SubmitionError = {};
    if (properties.length < 1) {
      errors.properties = 'Needs at least 1 Property';
      setOpenErrorModal(true);
    }
    if (validateProperty()) {
      errors.properties = 'Property missing info';
    }
    setErrors(errors);
    return Object.keys(errors).length === 0 ? null : errors;
  };
  const validateProperty = () => {
    const indexedPropertyErrors: IndexedPropertyErrors = properties.reduce((pre, p, index) => {
      let error: PropertyError = {};
      let hasError = false;
      if (!p.address) {
        error.address = 'Missing Address';
        hasError = true;
      }
      if ( 
        (!p.undeterminedLocality && !p.selectedCity?.municipalityId) ||
        (p.undeterminedLocality && !p.undeterminedLocality)
      ) {
        error.municipality = 'Missing Municipality';
        hasError = true;
      }
      if (hasError) {
        pre[index] = error;
      }
      return pre;
    }, {});
    setPropertyErrors(indexedPropertyErrors);

    const keys = Object.keys(indexedPropertyErrors);

    return keys.length > 0;
  };
  async function checkDuplicate(
    matchedProperty: Property
  ) {
    const params = qs.stringify({
      address:matchedProperty.address,
      stateCode:matchedProperty.selectedCity.stateCode,
      section: matchedProperty.section,
      block:matchedProperty.block,
      lot:matchedProperty.lot,
      qualifier:matchedProperty.qualifier,
      swisCode:matchedProperty.selectedCity.swisCode,
      rpadPropertyId:matchedProperty.rpadPropertyId
    });
  
    const { data } = await axios.get(`/api/clientproperty/duplicatecheck?${params}`)
    return data;
  }
    
  const onSubmit = async (orderId: number) => {
    const err = validateForm();
    if (!err) {
      const propertiesWithDuplicates = properties.map(async (p) => {
        const data = await checkDuplicate(p);
        return data.length > 0 ? {...p, duplicates: data.length, oldOrder: data[0]} : {...p}
      });
      const submitProperty = {
        properties: await Promise.all(propertiesWithDuplicates),
        searchTypes: addedSearches,
        note: clientNote,
        userId: user.id,
      };
      setSubmitting(true);
      await axios.post(`/api/ClientProperty/AddPropertyWithSearches/${orderId}`, submitProperty);
      setSubmitting(false);
      setProperties([createProperty()]);
      setAddedSearches([]);
      setClientNote('');
    }
};
  const onCancel = () => {
    setProperties([createProperty()]);
    setAddedSearches([]);
    setClientNote('');
  }
  const changePropertyInfo = (label: keyof Partial<Property>, value: any, index: number) => {
    const newProperties = produce(properties, draft => {
      (draft[index][label] as keyof Property) = value;
      if(label === 'selectedCity'){
        draft[index].municipalityId = value?.municipalityId;
        draft[index].subMunicipalityId = value?.subMunicipalityId;
        draft[index].hamletId = value?.hamletId;
      }
      if(labelsMarkUnvalidated.includes(label)){
        draft[index].isValidated = false;
        draft[index].printKey = null;
        draft[index].rpadPropertyId = null;

      }

    });
    setProperties(newProperties);
  };
  return (
    <AddPropertyContext.Provider
      value={{
        properties,
        setProperties,
        onSubmit,
        errors,
        propertyErrors,
        validateProperty,
        addedSearches,
        setAddedSearches,
        clientNote,
        setClientNote,
        openErrorModal,
        setOpenErrorModal,
        changePropertyInfo,
        submitting,
        onCancel
      }}
    >
      {props.children}
    </AddPropertyContext.Provider>
  );
};

export function useAddProperty() {
  const context = useContext(AddPropertyContext);
  return context;
}
